/*
 *  -----------------------------------------------------------------------
 *    <copyright file="rag-map/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import * as d3 from 'd3';
import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { RAGColourType } from '@types/graphql-global-types';
import Constants from '@utils/constants';
import Loading from '@components/core/loading';

const BLOCKED = '#888';

export default function RagMap({ id, labels, ragMap: map, includeDetectability, action, onChange }: any) {
    // We use useRef() for all these so that they retain their values between re-renders
    const svg = useRef(null);
    const zoom = useRef(null);
    const gGrid = useRef(null);
    const gDot = useRef(null);
    const gBlockedCols = useRef(null);
    const gBlockedRows = useRef(null);
    const gx = useRef(null);
    const gy = useRef(null);
    const invalidCols = useRef([] as any);
    const invalidRows = useRef([] as any);
    const ragMap = useRef(map ?? ([] as any));
    const option = useRef(action);

    // Update the current option value and rag map based on the component params
    option.current = action;
    ragMap.current = map;

    // Get an array of available likelihood and detectability values
    const likelihoodValues =
        labels?.likelihood?.filter((like: any) => like.enabled).map((like: any) => like.value) ?? [];
    const detectabilityValues = includeDetectability
        ? labels.detectability?.filter((d: any) => d.enabled).map((d: any) => d.value)
        : [1];

    const getMax = (values: any) =>
        Math.max.apply(
            null,
            values?.filter((v: any) => v.enabled).map((v: any) => v.value),
        );

    const drawRagMap = () => {
        const el = document.getElementById(id);
        if (!el) return;

        const width = 800;
        const height = 400;

        const margin = { top: 10, right: 10, bottom: 30, left: 30 };
        const w = (!isNaN(width) ? Math.max(width, 400) : 400) - margin.left - margin.right;
        const h = height - margin.top - margin.bottom;

        invalidRows.current = [];
        invalidCols.current = [0, 101, 102];

        const possibleCols = {} as any;
        for (let i = 0; i < 10; i++) for (let j = 0; j < 10; j++) possibleCols[(i * j).toString()] = true;

        // Get all of the valid columns
        const validCols = {} as any;
        for (let i = 0; i < likelihoodValues.length; i++)
            for (let j = 0; j < detectabilityValues.length; j++)
                validCols[(likelihoodValues[i] * detectabilityValues[j]).toString()] = true;

        // And fill in the invalid ones and unselected ones
        for (let i = 1; i <= 100; i++) if (!validCols[i.toString()]) invalidCols.current.push(i);

        // And all of the invalid rows
        labels.impact?.filter((i: any) => !i.enabled).forEach((i: any) => invalidRows.current.push(i.value));

        // Force the initial zoom extent to between 10-30
        const xExtent = Math.max(
            10,
            Math.min(30, getMax(labels.likelihood) * (includeDetectability ? getMax(labels.detectability) : 1)),
        );

        // Draw the grid
        const grid = (g: any, x: any, y: any) =>
            g
                .attr('stroke', 'currentColor')
                .attr('stroke-opacity', 0.1)
                .call((g2: any) =>
                    g2
                        .selectAll('.x')
                        .data(x.ticks(101))
                        .join(
                            (enter: any) => enter.append('line').attr('class', 'x').attr('y2', h),
                            (update: any) => update,
                            (exit: any) => exit.remove(),
                        )
                        .attr('x1', (d: any) => x(d - 1))
                        .attr('x2', (d: any) => x(d - 1))
                        .style('display', (d: any) => (parseInt(d, 10) === d ? 'block' : 'none')),
                )
                .call((g2: any) =>
                    g2
                        .selectAll('.y')
                        .data(y.ticks(11))
                        .join(
                            (enter: any) =>
                                enter
                                    .append('line')
                                    .attr('class', 'y')
                                    .attr('x1', margin.left / 2)
                                    .attr('x2', w + margin.left + margin.right),
                            (update: any) => update,
                            (exit: any) => exit.remove(),
                        )
                        .attr('y1', (d: any) => 0.5 + y(d))
                        .attr('y2', (d: any) => 0.5 + y(d)),
                );

        // Draw the Y-Axis
        const yAxis = (g: any, y: any) =>
            g
                .attr('transform', `translate(-10,-${h / 20})`)
                .call(d3.axisRight(y).tickValues(Array.from(Array(10), (_, i) => i + 1)))
                .call((g2: any) => g2.select('.domain').attr('display', 'none'));

        // Draw the X-Axis
        const xAxis = (g: any, x: any) =>
            g
                .attr('transform', `translate(${margin.left / 2},${h})`)
                .call(d3.axisBottom(x).tickValues(Array.from(Array(100), (_, i) => i + 1)))
                .call((g2: any) => g2.select('.domain').attr('display', 'none'));

        // Create the Y scale
        const y = d3.scaleLinear().domain([1, 11]).range([h, 0]);

        // Create the X scale
        const x = d3.scaleLinear().domain([0, 101]).range([0, w]);

        // Update positions and sizes on zoom
        function zoomed(event: any) {
            const { transform } = event;
            const zx = transform.rescaleX(x).interpolate(d3.interpolateRound);

            gDot.current
                .selectAll('rect')
                .attr('x', (d: any) => zx(d.x))
                .attr('width', zx(1) - zx(0));

            gBlockedCols.current
                .selectAll('rect')
                .attr('x', (d: any) => zx(d) - 1)
                .attr('width', zx(1) - zx(0) + 2);

            gBlockedRows.current
                .selectAll('rect')
                .attr('y', (d: any) => y(d + 1))
                .attr('height', y(0) - y(1));

            gx.current.call(xAxis, zx);
            gy.current.call(yAxis, y);
            gGrid.current.call(grid, zx, y);
        }

        // Set the initial zoom handler
        zoom.current = d3
            .zoom()
            .scaleExtent([3, 10])
            .translateExtent([
                [0, 0],
                [w, h],
            ])
            .extent([
                [0, 0],
                [w, h],
            ])
            .on('zoom', zoomed);

        const getColour = (c: RAGColourType) => {
            switch (c) {
                case RAGColourType.RED:
                    return 'red';
                case RAGColourType.AMBER:
                    return 'orange';
                case RAGColourType.GREEN:
                    return 'green';
                default:
                    return 'black';
            }
        };

        // This plots all of the rag map cells and blocks out all of the unavailable rows and columns
        const plot = () => {
            // Plot the RAG map cells
            const cell = gDot.current.selectAll('rect').data(ragMap.current);

            cell.exit().remove();

            cell.enter()
                .append('rect')
                .merge(cell)
                .attr('x', (d: any) => {
                    const transform = d3.zoomTransform(svg.current.node());
                    const zx = transform.rescaleX(x).interpolate(d3.interpolateRound);
                    return zx(d.x);
                })
                .attr('y', (d: any) => y(d.y + 1))
                .attr('height', Math.abs(y(0) - y(1)))
                .attr('width', () => {
                    const transform = d3.zoomTransform(svg.current.node());
                    const zx = transform.rescaleX(x).interpolate(d3.interpolateRound);
                    return zx(1) - zx(0);
                })
                .attr('stroke', BLOCKED)
                .attr('fill', (d: any) => getColour(d.colour));

            // Plot the blocked columns
            const cols = gBlockedCols.current.selectAll('rect').data(invalidCols.current);

            cols.enter()
                .append('rect')
                .merge(cols)
                .attr('x', (d: any) => {
                    const transform = d3.zoomTransform(svg.current.node());
                    const zx = transform.rescaleX(x).interpolate(d3.interpolateRound);
                    return zx(d) - 1;
                })
                .attr('y', 0)
                .attr('height', h)
                .attr('width', () => {
                    const transform = d3.zoomTransform(svg.current.node());
                    const zx = transform.rescaleX(x).interpolate(d3.interpolateRound);
                    return zx(1) - zx(0) + 2;
                })
                .attr('stroke', BLOCKED)
                .attr('fill', BLOCKED)
                .style('opacity', (d: any) => (possibleCols[d.toString()] ? 0.8 : 1));

            cols.exit().remove();

            // Plot the blocked rows
            const rows = gBlockedRows.current.selectAll('rect').data(invalidRows.current);

            rows.enter()
                .append('rect')
                .merge(rows)
                .attr('y', (d: any) => y(d + 1))
                .attr('x', 0)
                .attr('width', w + margin.left + margin.right)
                .attr('height', () => y(0) - y(1))
                .attr('stroke', BLOCKED)
                .attr('fill', BLOCKED)
                .style('opacity', 0.5);

            rows.exit().remove();
        };

        // Set the value of a single cell, update the plot and signal an onChange
        const setCell = (xx: number, yy: number, c: string) => {
            if (invalidCols.current.indexOf(xx) >= 0 || invalidRows.current.indexOf(yy) >= 0) return;

            const cell = ragMap.current.find((d: any) => d.x === xx && d.y === yy);
            if (cell) cell.color = c;
            else ragMap.current.push({ x: xx, y: yy, color: c });

            if (onChange)
                onChange({
                    x: xx,
                    y: yy,
                    c: c === 'green' ? RAGColourType.GREEN : c === 'red' ? RAGColourType.RED : RAGColourType.AMBER,
                });

            plot();
        };

        // Gets the current cell from the mouse position and paints it the selected colour
        const paintCurrentCell = (event: any) => {
            if (option.current === Constants.RagMapOptions.MOVE) return;

            const transform = d3.zoomTransform(svg.current.node());
            const xy = d3.pointer(event);
            const newXy = transform.invert(xy);
            const xIndex = Math.floor(x.invert(newXy[0]));
            const yIndex = Math.floor(y.invert(xy[1]));

            if (xIndex > 0 && yIndex > 0 && xIndex <= 100 && yIndex <= 10)
                setCell(
                    xIndex,
                    yIndex,
                    option.current === Constants.RagMapOptions.GREEN
                        ? 'green'
                        : option.current === Constants.RagMapOptions.RED
                          ? 'red'
                          : 'orange',
                );
        };

        // Resets the zoom extents
        const reset = () => {
            if (!svg.current) return;

            const z = svg.current
                .call(zoom.current as any)
                .transition()
                .duration(500)
                .call(
                    zoom.current.transform as any,
                    d3.zoomIdentity.scale(w / (x(xExtent) - x(0))).translate(-x(0), 0),
                );

            // If we're not in MOVE mode, disable the zoom controls
            if (option.current !== Constants.RagMapOptions.MOVE) z.call(() => svg.current.on('.zoom', null));
        };

        if (svg.current) {
            // If we're re-rendering, just reset and plot
            reset();
            plot();

            return;
        }

        // Create the main SVG element
        svg.current = d3
            .select('#rag-map')
            .append('svg')
            .attr('width', w + margin.left + margin.right)
            .attr('height', h + margin.bottom + margin.top)
            .attr('transform', `translate(${margin.left}, ${margin.top})`)
            .on('mousedown', (event: any) => {
                // Don't paint if we're not in MOVE mode
                if (option.current === Constants.RagMapOptions.MOVE) return false;

                // Colour the cell
                paintCurrentCell(event);

                // Paint on mousemove and turn off on mouseup
                const win = svg.current
                    .on('mousemove', paintCurrentCell)
                    .on('mouseup', () => win.on('mousemove', null).on('mouseup', null));

                // Prevent other events
                event.preventDefault();
            });

        // Create the grid canvas
        gGrid.current = svg.current.append('g');

        // And the cell canvas
        gDot.current = svg.current.append('g').attr('fill', 'none').attr('stroke-linecap', 'round');

        // The blocked column canvas
        gBlockedCols.current = svg.current.append('g').attr('fill', 'none').attr('stroke-linecap', 'round');

        // The blocked row canvas
        gBlockedRows.current = svg.current.append('g').attr('fill', 'none').attr('stroke-linecap', 'round');

        // The x and y axes
        gx.current = svg.current.append('g');
        gy.current = svg.current.append('g');

        // Plot and reset the first time
        plot();
        reset();
    };

    // When the model changes, draw the map
    useLayoutEffect(() => {
        if (map && labels) drawRagMap();
    }, [map, labels, includeDetectability]);

    // When the option changes, update the zoom handler
    useEffect(() => {
        if (!svg.current) return;

        if (option.current === Constants.RagMapOptions.MOVE) {
            if (zoom.current) svg.current.call(zoom.current);
        } else svg.current.on('.zoom', null);
    }, [option.current]);

    if (!map || !labels) return <Loading />;

    // The div that the SVG will be rendered into
    return <div id={id} className="scoring-map" />;
}
